import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { Button, Table, Form, Card, Alert, InputGroup, Badge, Dropdown } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useStores } from "../../context/mobx-context";
import UtmConfigModal from "./UtmConfigModal";
import BulkUtmModal from "./BulkUtmModal";

function AllDeeplinks() {
  const { allDeeplinksStore } = useStores();
  const [searchInput, setSearchInput] = useState("");
  const [showAlert, setShowAlert] = useState(null);
  const [selectedDeeplink, setSelectedDeeplink] = useState(null);
  const [showUtmModal, setShowUtmModal] = useState(false);
  const [showBulkUtmModal, setShowBulkUtmModal] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (allDeeplinksStore.deeplinks.length === 0) {
      allDeeplinksStore.fetchDeeplinks();
    }
    // Sync search input with store
    setSearchInput(allDeeplinksStore.searchQuery);
  }, [allDeeplinksStore]);

  const handleSearch = (e) => {
    e.preventDefault();
    allDeeplinksStore.setSearchQuery(searchInput);
  };

  const clearSearch = () => {
    setSearchInput("");
    allDeeplinksStore.setSearchQuery("");
  };

  const handleCreateShortUrl = async (deeplink) => {
    try {
      const result = await allDeeplinksStore.createShortUrl(deeplink.url, deeplink.path);
      setShowAlert({
        type: "success",
        message: `Short URL created: ${result.shortUrl}`,
      });
    } catch (error) {
      setShowAlert({
        type: "danger",
        message: error.message || "Failed to create short URL",
      });
    }
  };

  const handleViewAnalytics = (deeplink) => {
    // Navigate to the dedicated analytics page for this specific deeplink
    history.push(`/deeplinks/analytics/${encodeURIComponent(deeplink.path)}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setShowAlert({ type: "success", message: "Copied to clipboard!" });
      })
      .catch(() => {
        setShowAlert({ type: "danger", message: "Failed to copy to clipboard" });
      });
  };

  const handleManageUtm = (deeplink) => {
    setSelectedDeeplink(deeplink);
    setShowUtmModal(true);
  };

  const loadUtmConfigs = async (deeplink) => {
    // Load UTM configs if not already loaded
    if (!deeplink.utmConfigurations) {
      try {
        await allDeeplinksStore.fetchUtmConfigs(deeplink.path);
      } catch (error) {
        setShowAlert({
          type: "danger",
          message: `Failed to load UTM configurations: ${error.message}`,
        });
      }
    }
  };

  // Load UTM configs for all deeplinks on mount
  useEffect(() => {
    if (allDeeplinksStore.deeplinks.length > 0) {
      allDeeplinksStore.deeplinks.forEach((deeplink) => {
        if (!deeplink.utmConfigurations) {
          loadUtmConfigs(deeplink);
        }
      });
    }
  }, [allDeeplinksStore.deeplinks]);

  return (
    <div className="container-fluid px-4 py-3">
      <div className="mb-4 p-3 bg-light rounded">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2 className="mb-1">🔗 All Available Deeplinks</h2>
            <p className="text-muted mb-0">Manage and monitor all deeplinks in the system</p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="success"
              size="lg"
              onClick={() => setShowBulkUtmModal(true)}
              className="d-flex align-items-center gap-2 shadow-sm"
              disabled={allDeeplinksStore.creatingBulkUtm}
            >
              {allDeeplinksStore.creatingBulkUtm ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Creating...
                </>
              ) : (
                <>
                  <span>🌍</span>
                  Create Universal UTM
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Bulk UTM Info Banner */}
        <div className="mt-3 p-3 bg-info bg-opacity-10 border border-info border-opacity-25 rounded">
          <div className="d-flex align-items-start gap-3">
            <span className="fs-4">💡</span>
            <div className="flex-grow-1">
              <h6 className="mb-1 text-info">Universal UTM Campaigns</h6>
              <p className="mb-2 small text-muted">
                Create the same UTM tracking across ALL {allDeeplinksStore.totalDeeplinks} deeplinks at once.
              </p>
            </div>
          </div>
        </div>
      </div>

      {showAlert && (
        <Alert variant={showAlert.type} onClose={() => setShowAlert(null)} dismissible className="mb-4 shadow-sm">
          <div className="d-flex align-items-center">
            <span className="me-2">{showAlert.type === "success" ? "✅" : "❌"}</span>
            {showAlert.message}
          </div>
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary text-white">
          <h5 className="mb-0">🔍 Search Deeplinks</h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Form onSubmit={handleSearch}>
            <InputGroup size="lg">
              <Form.Control
                type="text"
                placeholder="Search deeplinks by path or description..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="fs-6"
              />
              <Button variant="outline-dark" type="submit" className="px-4">
                🔍 Search
              </Button>
              {allDeeplinksStore.searchQuery && (
                <Button variant="outline-secondary" onClick={clearSearch} className="px-3">
                  ✖️ Clear
                </Button>
              )}
            </InputGroup>
            <Form.Text className="text-muted mt-2">
              Search across deeplink paths and descriptions to find specific links quickly.
            </Form.Text>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header className="bg-dark text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">📋 Deeplinks Directory</h5>
            <small className="text-light">{allDeeplinksStore.totalDeeplinks} total deeplinks</small>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {allDeeplinksStore.isFetching ? (
            <div className="text-center p-5">
              <div className="spinner-border text-secondary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="text-muted mb-2">Loading Deeplinks</h5>
              <p className="text-muted mb-0">Please wait while we fetch all available deeplinks...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th className="text-start px-4 py-3">#</th>
                    <th className="text-start px-4 py-3">Path</th>
                    <th className="text-start px-4 py-3">Description</th>
                    <th className="text-start px-4 py-3">Full URL</th>
                    <th className="text-start px-4 py-3">Short URL</th>
                    <th className="text-start px-4 py-3">UTM Configs</th>
                    <th className="text-start px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allDeeplinksStore.deeplinks.map((deeplink, index) => (
                    <tr key={deeplink.path} className={index % 2 === 0 ? "table-light" : ""}>
                      <td className="text-start px-4 py-3">
                        <span className="badge bg-secondary">
                          {(allDeeplinksStore.currentPage - 1) * allDeeplinksStore.pageSize + index + 1}
                        </span>
                      </td>
                      <td className="text-start px-4 py-3">
                        <code className="bg-light border px-2 py-1 rounded fw-bold text-dark">{deeplink.path}</code>
                      </td>
                      <td className="text-start px-4 py-3" style={{ maxWidth: "300px" }}>
                        <p className="mb-0 text-wrap">{deeplink.description}</p>
                      </td>
                      <td className="text-start px-4 py-3" style={{ maxWidth: "250px" }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <a
                            href={deeplink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none flex-grow-1"
                          >
                            <code className="text-primary text-wrap d-block fw-semibold">{deeplink.url}</code>
                          </a>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => copyToClipboard(deeplink.url)}
                            className="mt-1"
                          >
                            📋
                          </Button>
                        </div>
                      </td>
                      <td className="text-start px-4 py-3" style={{ maxWidth: "200px" }}>
                        {deeplink.shortUrl ? (
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <a
                              href={deeplink.shortUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              <code className="text-success d-block text-wrap">{deeplink.shortUrl}</code>
                            </a>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => copyToClipboard(deeplink.shortUrl)}
                              className="mt-1"
                            >
                              📋 Copy
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">No short URL</span>
                        )}
                      </td>
                      <td className="text-start px-4 py-3">
                        <div>
                          {allDeeplinksStore.loadingUtmConfigs.has(deeplink.path) ? (
                            <div className="text-center py-2">
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Loading...
                            </div>
                          ) : deeplink.utmConfigurations && deeplink.utmConfigurations.length > 0 ? (
                            <ul className="list-unstyled mb-0">
                              {deeplink.utmConfigurations.map((config, idx) => (
                                <li key={idx} className="mb-2">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="flex-grow-1">
                                      <strong className="d-block small">{config.name}</strong>
                                      <div className="d-flex gap-2 align-items-center">
                                        <Badge bg="primary" className="small">
                                          {config.utmSource}
                                        </Badge>
                                        {config.utmMedium && (
                                          <Badge bg="secondary" className="small">
                                            {config.utmMedium}
                                          </Badge>
                                        )}
                                        {config.utmCampaign && (
                                          <Badge bg="info" className="small">
                                            {config.utmCampaign}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => copyToClipboard(config.url)}
                                      title={config.url}
                                    >
                                      Copy
                                    </Button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </div>
                      </td>
                      <td className="text-start px-4 py-3">
                        <div className="d-flex gap-2 flex-wrap">
                          {!deeplink.shortUrl && (
                            <Button
                              variant="outline-dark"
                              size="sm"
                              onClick={() => handleCreateShortUrl(deeplink)}
                              disabled={allDeeplinksStore.creatingShortUrls.has(deeplink.path)}
                              className="mb-1"
                            >
                              {allDeeplinksStore.creatingShortUrls.has(deeplink.path)
                                ? "⏳ Creating..."
                                : "🔗 Create Short"}
                            </Button>
                          )}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleManageUtm(deeplink)}
                            className="mb-1"
                          >
                            ⚙️ Manage UTM
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleViewAnalytics(deeplink)}
                            className="mb-1"
                          >
                            📊 Analytics
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {allDeeplinksStore.hasFetched && allDeeplinksStore.deeplinks.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center p-5">
                        <div className="mb-3">
                          <span className="display-1">🔍</span>
                        </div>
                        <h4 className="text-muted mb-3">No Deeplinks Found</h4>
                        <p className="text-muted mb-0">
                          {allDeeplinksStore.searchQuery
                            ? `No deeplinks match your search for "${allDeeplinksStore.searchQuery}"`
                            : "No deeplinks are available in the system"}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {allDeeplinksStore.totalDeeplinks > allDeeplinksStore.pageSize && (
        <Card className="mt-4 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">Pagination</h6>
                <small className="text-muted">
                  Showing {Math.min(allDeeplinksStore.deeplinks.length, allDeeplinksStore.pageSize)} of{" "}
                  {allDeeplinksStore.totalDeeplinks} deeplinks
                </small>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="outline-secondary"
                  disabled={allDeeplinksStore.currentPage === 1 || allDeeplinksStore.isFetching}
                  onClick={() => allDeeplinksStore.setCurrentPage(allDeeplinksStore.currentPage - 1)}
                >
                  ← Previous
                </Button>
                <span className="badge bg-secondary px-3 py-2 fs-6">
                  Page {allDeeplinksStore.currentPage} of {allDeeplinksStore.totalPages}
                </span>
                <Button
                  variant="outline-secondary"
                  disabled={
                    allDeeplinksStore.currentPage >= allDeeplinksStore.totalPages || allDeeplinksStore.isFetching
                  }
                  onClick={() => allDeeplinksStore.setCurrentPage(allDeeplinksStore.currentPage + 1)}
                >
                  Next →
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {showUtmModal && (
        <UtmConfigModal
          show={showUtmModal}
          onHide={() => {
            setShowUtmModal(false);
            setSelectedDeeplink(null);
          }}
          deeplink={selectedDeeplink}
          store={allDeeplinksStore}
        />
      )}

      {showBulkUtmModal && (
        <BulkUtmModal show={showBulkUtmModal} onHide={() => setShowBulkUtmModal(false)} store={allDeeplinksStore} />
      )}
    </div>
  );
}

export default observer(AllDeeplinks);
